import {ZxcvbnFactory} from '@zxcvbn-ts/core';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en';

const MIN_PASSWORD_LENGTH = 12;
const MIN_ZXCVBN_SCORE = 3;

// Composition policy (upper/lower/digit/special), independent of zxcvbn's entropy-based scoring below.
const passwordComplexityRegex =
  /^(?=.*[a-z])(?=.*\d)(?=.*[A-Z])(?=.*[!@#$%^&*\-=.,'"[\](){}?_/\\;:|<>~`+])/;

const zxcvbn = new ZxcvbnFactory({
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
  },
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  // No `translations` option: zxcvbn then returns feedback as stable keys
  // (e.g. 'topTen', 'dates') instead of pre-translated English sentences,
  // so callers can map them into their own i18n system.
});

export type PasswordStrengthFailureReason =
  'too_short' | 'missing_complexity' | 'too_guessable';

// Buckets the many zxcvbn feedback keys into a small set of actionable hints
// that callers can translate for the "too_guessable" case.
export type PasswordGuessabilityHint =
  'common_word' | 'pattern' | 'dates' | 'pwned';

const guessabilityHintByZxcvbnKey: Record<string, PasswordGuessabilityHint> = {
  topTen: 'common_word',
  topHundred: 'common_word',
  common: 'common_word',
  similarToCommon: 'common_word',
  wordByItself: 'common_word',
  namesByThemselves: 'common_word',
  commonNames: 'common_word',
  userInputs: 'common_word',
  anotherWord: 'common_word',
  useWords: 'common_word',
  noNeed: 'common_word',
  straightRow: 'pattern',
  keyPattern: 'pattern',
  simpleRepeat: 'pattern',
  extendedRepeat: 'pattern',
  sequences: 'pattern',
  longerKeyboardPattern: 'pattern',
  repeated: 'pattern',
  l33t: 'pattern',
  reverseWords: 'pattern',
  allUppercase: 'pattern',
  capitalization: 'pattern',
  dates: 'dates',
  recentYears: 'dates',
  associatedYears: 'dates',
  pwned: 'pwned',
};

export const getPasswordGuessabilityHint = (feedback: {
  warning: string | null;
  suggestions: string[];
}): PasswordGuessabilityHint | undefined => {
  const key = feedback.warning || feedback.suggestions[0];
  return key ? guessabilityHintByZxcvbnKey[key] : undefined;
};

export type PasswordStrengthResult =
  | {valid: true}
  | {valid: false; reason: 'too_short' | 'missing_complexity'}
  | {
      valid: false;
      reason: 'too_guessable';
      hint?: PasswordGuessabilityHint;
    };

export const validatePasswordStrength = (
  password: string
): PasswordStrengthResult => {
  if (!password || password.length < MIN_PASSWORD_LENGTH) {
    return {valid: false, reason: 'too_short'};
  }

  if (!passwordComplexityRegex.test(password)) {
    return {valid: false, reason: 'missing_complexity'};
  }

  const result = zxcvbn.check(password);
  if (result.score < MIN_ZXCVBN_SCORE) {
    return {
      valid: false,
      reason: 'too_guessable',
      hint: getPasswordGuessabilityHint(result.feedback),
    };
  }

  return {valid: true};
};
